/**
 * URL of the public Help Center for the current environment, derived from the
 * current host (there is no build-time config for this — the app keys off
 * window.location everywhere).
 *
 *   staging.noviapay.com / noviapay.com   ->  https://docs.noviapay.com[/slug]
 *   docs.* / docs-staging.* (on docs)     ->  [/slug]   (already on the docs host)
 *   localhost / other (local dev)         ->  /help[/slug]   (no docs subdomain locally)
 *
 * @param slug Path inside the Help Center, e.g. "echipa/permisiuni" (no leading
 *             slash). Omit for the Help Center home.
 */
export function helpCenterUrl(slug?: string): string {
  const seg = slug ? `/${slug.replace(/^\/+/, '')}` : '';
  const { hostname } = window.location;
  if (/^docs[-.]/.test(hostname)) return seg || '/';
  if (hostname.endsWith('noviapay.com')) return `https://docs.noviapay.com${seg}`;
  return `/help${seg}`;
}

/**
 * Base URL for Help Center media (demo videos, poster frames) on deployed
 * hosts. Served straight from the app's public S3 bucket (NOV-444 —
 * CloudFront was skipped after the distribution request stalled; S3 serves
 * HTTP ranges natively, so <video> seeking works without it). If a CDN is
 * ever put in front, point this at the distribution URL — no other change.
 *
 * Only deployed hosts (*.noviapay.com) use this base; local dev always plays
 * from the gitignored `public/help-media/` folder so a freshly recorded video
 * is verifiable before it's uploaded. If the base is ever emptied again,
 * deployed hosts fall back to the "coming soon" placeholder.
 */
const HELP_MEDIA_BASE = 'https://business-ai-agent.s3.eu-central-1.amazonaws.com/help-media';

/**
 * Resolve an article's video/poster reference to a playable URL. Bare
 * filenames ("gs-what.mp4") resolve against HELP_MEDIA_BASE on deployed
 * hosts, and against the app host's /help-media (i.e. `public/help-media/`)
 * in local dev — always, so new recordings are verifiable before upload.
 * Absolute http(s) URLs pass through unchanged; empty stays empty.
 */
export function helpMediaUrl(ref?: string): string {
  if (!ref) return '';
  if (/^https?:\/\//i.test(ref)) return ref;
  const file = ref.replace(/^\/+/, '');
  if (window.location.hostname.endsWith('noviapay.com')) {
    return HELP_MEDIA_BASE ? `${HELP_MEDIA_BASE}/${file}` : '';
  }
  return `/help-media/${file}`;
}
